import { Box, Menu, MenuItem, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Editor } from "@tiptap/react";
import React from "react";
import { useTranslation } from "@localization/useTranslation";
import { useTableControls } from "./MentionInputTableControls.hooks.ts";
import { GRIP_GAP, GRIP_LENGTH, GRIP_THICKNESS, useMentionInputTableControlsStyles } from "./MentionInputTableControls.styles.ts";

export interface MentionInputTableControlsProps {
    editor: Editor;
    display: boolean;
    onFocusChange: (focus: boolean) => void;
}

const INDICATOR_THICKNESS = 2;

const stopEditorBlur = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
};

export default function MentionInputTableControls({ editor, display, onFocusChange }: MentionInputTableControlsProps) {
    const { translateString } = useTranslation();
    const styles = useMentionInputTableControlsStyles();
    const controls = useTableControls(editor, onFocusChange);
    const { tableState, geometry, dragState, menu, overlayRef, rowIndex, columnIndex, isRowDraggable, gripHandlers, closeMenu, actions } = controls;

    if (!display || !tableState) {
        return null;
    }

    const menuItemProps = { onMouseDown: stopEditorBlur };
    const showGrips = geometry && !tableState.hasMergedCells;

    return (
        <Box
            ref={overlayRef}
            sx={styles.overlay}
            style={
                geometry
                    ? { top: geometry.overlay.top, left: geometry.overlay.left, width: geometry.overlay.width, height: geometry.overlay.height }
                    : { top: 0, left: 0, width: 0, height: 0 }
            }
            onMouseDown={(event) => event.preventDefault()}
        >
            {geometry && (
                <>
                    <Tooltip title={translateString("addRow")}>
                        <Box
                            sx={styles.addRowBar}
                            style={{ top: geometry.table.top + geometry.table.height, left: geometry.table.left, width: geometry.table.width }}
                            onClick={(event) => {
                                // Without this the click also reaches the editor's own handler, which
                                // reads a click under the table as "put the caret after it".
                                event.stopPropagation();
                                actions.addRowAtEnd();
                            }}
                        >
                            <AddIcon fontSize="inherit" />
                        </Box>
                    </Tooltip>
                    <Tooltip title={translateString("addColumn")}>
                        <Box
                            sx={styles.addColumnBar}
                            style={{ top: geometry.table.top, left: geometry.table.left + geometry.table.width, height: geometry.table.height }}
                            onClick={actions.addColumnAtEnd}
                        >
                            <AddIcon fontSize="inherit" />
                        </Box>
                    </Tooltip>
                </>
            )}

            {showGrips && geometry.row && (
                <Box
                    sx={[styles.rowGrip, !isRowDraggable && styles.gripNotDraggable, dragState?.axis === "row" && styles.gripDragging]}
                    style={{
                        top: geometry.row.top + geometry.row.height / 2 - GRIP_LENGTH / 2,
                        left: geometry.table.left - GRIP_GAP - GRIP_THICKNESS,
                    }}
                    {...gripHandlers("row", rowIndex, isRowDraggable)}
                />
            )}
            {showGrips && geometry.column && (
                <Box
                    sx={[styles.columnGrip, dragState?.axis === "column" && styles.gripDragging]}
                    style={{
                        left: geometry.column.left + geometry.column.width / 2 - GRIP_LENGTH / 2,
                        top: geometry.table.top - GRIP_GAP - GRIP_THICKNESS,
                    }}
                    {...gripHandlers("column", columnIndex, true)}
                />
            )}

            {geometry && dragState?.axis === "row" && (
                <Box
                    sx={styles.dropIndicator}
                    style={{
                        top: dragState.indicatorOffset - INDICATOR_THICKNESS / 2,
                        left: geometry.table.left,
                        width: geometry.table.width,
                        height: INDICATOR_THICKNESS,
                    }}
                />
            )}
            {geometry && dragState?.axis === "column" && (
                <Box
                    sx={styles.dropIndicator}
                    style={{
                        left: dragState.indicatorOffset - INDICATOR_THICKNESS / 2,
                        top: geometry.table.top,
                        height: geometry.table.height,
                        width: INDICATOR_THICKNESS,
                    }}
                />
            )}

            <Menu
                anchorEl={menu?.anchorEl ?? null}
                open={menu?.axis === "row"}
                onClose={closeMenu}
                slotProps={{ paper: { sx: styles.menuPaper, onMouseDown: stopEditorBlur } }}
            >
                <MenuItem {...menuItemProps} onClick={actions.insertRowAbove} disabled={!controls.canInsertRowAbove}>
                    {translateString("insertRowAbove")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={actions.insertRowBelow}>
                    {translateString("insertRowBelow")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={() => actions.moveRow(-1)} disabled={!controls.canMoveRowUp}>
                    {translateString("moveRowUp")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={() => actions.moveRow(1)} disabled={!controls.canMoveRowDown}>
                    {translateString("moveRowDown")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={actions.deleteRow} disabled={!controls.canDeleteRow}>
                    {translateString("deleteRow")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={actions.deleteTable}>
                    {translateString("deleteTable")}
                </MenuItem>
            </Menu>

            <Menu
                anchorEl={menu?.anchorEl ?? null}
                open={menu?.axis === "column"}
                onClose={closeMenu}
                slotProps={{ paper: { sx: styles.menuPaper, onMouseDown: stopEditorBlur } }}
            >
                <MenuItem {...menuItemProps} onClick={actions.insertColumnLeft}>
                    {translateString("insertColumnLeft")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={actions.insertColumnRight}>
                    {translateString("insertColumnRight")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={() => actions.moveColumn(-1)} disabled={!controls.canMoveColumnLeft}>
                    {translateString("moveColumnLeft")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={() => actions.moveColumn(1)} disabled={!controls.canMoveColumnRight}>
                    {translateString("moveColumnRight")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={actions.deleteColumn}>
                    {translateString("deleteColumn")}
                </MenuItem>
                <MenuItem {...menuItemProps} onClick={actions.deleteTable}>
                    {translateString("deleteTable")}
                </MenuItem>
            </Menu>
        </Box>
    );
}
